import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ticket, Minus, Plus, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { getReferralCode, resolveReferral } from "@/lib/referral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketDialog({ open, onOpenChange, event }) {
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [referredBy, setReferredBy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQty(1);
    setName("");
    setEmail("");
    // Attempt to resolve referral for a warm "Referred by Marcus" banner
    const code = getReferralCode();
    if (code) {
      resolveReferral(API, code).then((r) => r && setReferredBy(r));
    } else {
      setReferredBy(null);
    }
  }, [open]);

  if (!event) return null;

  const price = Number(event.ticket_price || 0);
  const total = (price * qty).toFixed(2);
  const capacityLeft =
    event.ticket_capacity != null
      ? Math.max(0, event.ticket_capacity - (event.tickets_sold || 0))
      : null;
  const maxQty = Math.min(20, capacityLeft ?? 20);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const code = getReferralCode();
      const { data } = await axios.post(`${API}/tickets/checkout`, {
        event_id: event.id,
        quantity: qty,
        buyer_name: name.trim(),
        buyer_email: email.trim(),
        referrer_code: code || null,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        // Redirect to Stripe-hosted checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error("Couldn't open checkout. Try again.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start checkout");
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="ticket-dialog">
        <DialogHeader>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-1 flex items-center gap-2">
            <Ticket className="w-3.5 h-3.5" /> Buy Tickets
          </div>
          <DialogTitle className="font-display uppercase text-2xl tracking-tight">
            {event.title}
          </DialogTitle>
          <DialogDescription>
            {event.event_date}
            {event.event_time ? ` · ${event.event_time}` : ""}
            {capacityLeft != null && (
              <span className="ml-2 text-[var(--trio-primary)] font-semibold">
                {capacityLeft} left
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {referredBy && (
          <div
            data-testid="ticket-dialog-referrer"
            className="text-xs bg-[var(--trio-accent)]/15 border border-[var(--trio-accent)]/40 px-3 py-2 rounded"
          >
            Referred by <span className="font-semibold">{referredBy.name}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="ticket-dialog-form">
          <div>
            <Label htmlFor="ticket-name" className="text-xs tracking-widest uppercase">
              Full Name
            </Label>
            <Input
              id="ticket-name"
              data-testid="ticket-buyer-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1.5 rounded-none"
            />
          </div>
          <div>
            <Label htmlFor="ticket-email" className="text-xs tracking-widest uppercase">
              Email
            </Label>
            <Input
              id="ticket-email"
              type="email"
              data-testid="ticket-buyer-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 rounded-none"
            />
          </div>

          <div>
            <Label className="text-xs tracking-widest uppercase">Quantity</Label>
            <div className="mt-1.5 flex items-center gap-4">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={qty <= 1}
                data-testid="ticket-qty-minus"
                className="w-10 h-10 border border-[var(--trio-text)]/25 hover:border-[var(--trio-primary)] disabled:opacity-40 flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <div className="text-2xl font-display tracking-tight w-10 text-center" data-testid="ticket-qty">
                {qty}
              </div>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                disabled={qty >= maxQty}
                data-testid="ticket-qty-plus"
                className="w-10 h-10 border border-[var(--trio-text)]/25 hover:border-[var(--trio-primary)] disabled:opacity-40 flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
              <div className="ml-auto text-right">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--trio-muted)]">
                  ${price.toFixed(2)} × {qty}
                </div>
                <div className="font-display text-3xl text-[var(--trio-primary)]" data-testid="ticket-total">
                  ${total}
                </div>
                <div className="text-[9px] text-[var(--trio-muted)] uppercase tracking-widest">+ tax at checkout</div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="ticket-checkout-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-3 text-sm tracking-widest uppercase font-bold disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…
              </>
            ) : (
              <>
                Pay ${total} <ExternalLink className="w-4 h-4" />
              </>
            )}
          </button>
          <p className="text-[10px] text-center uppercase tracking-widest text-[var(--trio-muted)]">
            Secure checkout by Stripe
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
